import React from "react"
import Swal from "sweetalert2"
import { toast } from "react-toastify"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../../AppContext"
import { CONST_USER_ROLE_DAF, CONST_USER_ROLE_DG, CONST_USER_ROLE_RESPONSABLE_RH, CONST_USER_ROLE_RH, CONST_USER_ROLE_SUPER_ADMIN } from "@common-constants/*"
import { IPersonnelDemandeEmbaucheModel } from "../core/_models"
import {
     personnelDESoumettreRequest,
     personnelDEValiderDgRequest,
     personnelDERectifierDgRequest,
     personnelDEValiderHrRequest,
     personnelDERectifierHrRequest,
     personnelDESupprimerRequest,
} from "../core/_requests"
import {
     ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_FINALISER,
     ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_DOCUMENTS_EMBAUCHE,
} from "../../gestion/routing/routing-endpoints"

const ETAT_BROUILLON = "ETAT_BROUILLON"
const ETAT_SOUMIS_DG = "ETAT_SOUMIS_DG"
const ETAT_VALIDE_DG = "ETAT_VALIDE_DG"
const ETAT_RECTIF_PAR_DG = "ETAT_RECTIF_PAR_DG"
const ETAT_RECTIF_PAR_HR = "ETAT_RECTIF_PAR_HR"
const ETAT_VALIDE_HR = "ETAT_VALIDE_HR"
const ETAT_FINALISE = "ETAT_FINALISE"

type Props = {
     demande: IPersonnelDemandeEmbaucheModel
     onDone: () => void // rafraîchissement après une action réussie
     onDeleted?: () => void // après abandon (la demande n'existe plus)
     className?: string
}

const DemandeEmbaucheActions = ({ demande, onDone, onDeleted, className }: Props) => {
     const { currentUser } = useAuth()
     const navigate = useNavigate()
     const roles = currentUser?.roles ?? []

     const isSuperAdmin = roles.includes(CONST_USER_ROLE_SUPER_ADMIN)
     // Validation/rectification DG : uniquement ROLE_DG. RH : uniquement DAF / Responsable RH / RH.
     const canActDg = roles.includes(CONST_USER_ROLE_DG)
     const canActHr = roles.includes(CONST_USER_ROLE_DAF) || roles.includes(CONST_USER_ROLE_RESPONSABLE_RH) || roles.includes(CONST_USER_ROLE_RH)

     // Action en cours : verrouille tous les boutons + loader ciblé sur celui de l'action.
     const [busyAction, setBusyAction] = React.useState<string | null>(null)
     const busy = busyAction !== null
     const spin = (action: string, label: React.ReactNode) =>
          busyAction === action ? (
               <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" /> {label}
               </>
          ) : (
               label
          )

     function runAction(promise: Promise<any>, successMsg: string, action: string, deleted = false) {
          setBusyAction(action)
          toast.info("Traitement en cours…", { autoClose: false })
          promise
               .then(() => {
                    toast.dismiss()
                    toast.success(successMsg)
                    if (deleted) onDeleted?.()
                    else onDone()
               })
               .catch(e => {
                    toast.dismiss()
                    toast.error(e?.response?.data?.detail ?? "Une erreur est survenue.", { autoClose: false })
               })
               .finally(() => setBusyAction(null))
     }

     function handleSoumettre() {
          const rectif = [ETAT_RECTIF_PAR_DG, ETAT_RECTIF_PAR_HR].includes(demande.etat ?? "")
          if (rectif) {
               Swal.fire({
                    icon: "question",
                    title: "Envoyer la demande corrigée ?",
                    input: "textarea",
                    inputLabel: "Commentaire (facultatif)",
                    inputPlaceholder: "Précisez les corrections apportées…",
                    showCancelButton: true,
                    confirmButtonText: "Envoyer",
                    cancelButtonText: "Annuler",
               }).then(res => res.isConfirmed && runAction(personnelDESoumettreRequest(demande.id, (res.value ?? "").trim() || undefined), "La demande d'embauche a bien été envoyée", "soumettre"))
               return
          }
          Swal.fire({
               icon: "question",
               title: "Voulez-vous soumettre la demande d'embauche ?",
               text: "Tous les champs et documents requis doivent être renseignés. La soumission sera bloquée sinon.",
               input: "textarea",
               inputLabel: "Commentaire (facultatif)",
               inputPlaceholder: "Ajouter un commentaire…",
               showCancelButton: true,
               confirmButtonText: "Soumettre",
               cancelButtonText: "Annuler",
          }).then(res => res.isConfirmed && runAction(personnelDESoumettreRequest(demande.id, (res.value ?? "").trim() || undefined), "La demande d'embauche a bien été envoyée", "soumettre"))
     }

     function handleValiderDg() {
          const reexamen = !!demande.isDgReexamen
          Swal.fire({
               icon: "question",
               title: reexamen ? "Revalider cette demande d'embauche ?" : "Valider cette demande d'embauche ?",
               text: reexamen ? "Les RH ont renvoyé cette demande pour un nouvel examen. Elle repartira ensuite vers les RH." : undefined,
               input: "textarea",
               inputLabel: "Commentaire (facultatif)",
               inputPlaceholder: reexamen ? "Précisez votre réponse au renvoi des RH…" : "Ajouter un commentaire…",
               showCancelButton: true,
               confirmButtonText: reexamen ? "Revalider" : "Valider",
               cancelButtonText: "Annuler",
          }).then(res => res.isConfirmed && runAction(personnelDEValiderDgRequest(demande.id, (res.value ?? "").trim() || undefined), "Demande validée.", "validerDg"))
     }

     function handleValiderHr() {
          const hasContrat = ((demande.personnel as any)?.personnelDocuments ?? []).some((d: any) => d.type === "TYPE_CONTRAT" && !d.isDeleted && d.googleDriveID)
          Swal.fire({
               icon: "question",
               title: "Valider cette demande d'embauche ?",
               input: "file",
               inputLabel: hasContrat ? "Remplacer le contrat de travail (facultatif)" : "Contrat de travail (obligatoire)",
               inputAttributes: { accept: "application/pdf,image/*", "aria-label": "Contrat de travail" },
               inputValidator: v => (!v && !hasContrat ? "Le contrat de travail est requis pour valider." : undefined),
               showCancelButton: true,
               confirmButtonText: "Valider",
               cancelButtonText: "Annuler",
          }).then(res => res.isConfirmed && runAction(personnelDEValiderHrRequest(demande.id, (res.value as File) || undefined), "Demande validée.", "validerHr"))
     }

     function handleRectifierDg() {
          Swal.fire({
               icon: "warning",
               title: "Demander une rectification",
               input: "textarea",
               inputLabel: "Commentaire (obligatoire)",
               inputPlaceholder: "Précisez ce qui doit être corrigé…",
               showCancelButton: true,
               confirmButtonText: "Envoyer",
               cancelButtonText: "Annuler",
               inputValidator: v => (!v || !v.trim() ? "Un commentaire est requis" : undefined),
          }).then(res => res.isConfirmed && res.value && runAction(personnelDERectifierDgRequest(demande.id, res.value.trim()), "Rectification demandée.", "rectifierDg"))
     }

     function handleRectifierHr() {
          const creatorName = (demande.requester?.prenomNom ?? "au créateur").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string))
          // Créateur DG : la demande n'est jamais passée par la DG → la rectification revient toujours au créateur
          // (pas de choix créateur/DG), on affiche simplement le destinataire.
          const cibleChoixHtml = demande.creatorIsDg
               ? `<div class="text-muted text-start fs-6">La demande sera envoyée à <b>${creatorName}</b>.</div>`
               : `<select id="de-rectif-cible" class="form-select"><option value="creator">Renvoyer à ${creatorName}</option><option value="dg">Renvoyer à la DG</option></select>`
          Swal.fire({
               icon: "warning",
               title: "Demander une rectification",
               html: `<textarea id="de-rectif-comment" class="form-control mb-3" style="min-height:130px" placeholder="Commentaire (obligatoire)…"></textarea>` + cibleChoixHtml,
               showCancelButton: true,
               confirmButtonText: "Envoyer",
               cancelButtonText: "Annuler",
               didOpen: () => (document.getElementById("de-rectif-comment") as HTMLTextAreaElement)?.focus(),
               preConfirm: () => {
                    const comment = (document.getElementById("de-rectif-comment") as HTMLTextAreaElement)?.value?.trim()
                    // Pas de select pour un créateur DG → cible « creator » par défaut.
                    const cible = ((document.getElementById("de-rectif-cible") as HTMLSelectElement)?.value ?? "creator") as "creator" | "dg"
                    if (!comment) {
                         Swal.showValidationMessage("Un commentaire est requis")
                         return false
                    }
                    return { comment, cible }
               },
          }).then(res => res.isConfirmed && res.value && runAction(personnelDERectifierHrRequest(demande.id, res.value.comment, res.value.cible), "Rectification demandée.", "rectifierHr"))
     }

     function handleSupprimer() {
          Swal.fire({
               icon: "warning",
               title: "Abandonner cette demande ?",
               text: "La demande et le collaborateur provisoire seront supprimés. Action irréversible.",
               showCancelButton: true,
               confirmButtonText: "Abandonner",
               cancelButtonText: "Annuler",
               confirmButtonColor: "#d33",
          }).then(res => res.isConfirmed && runAction(personnelDESupprimerRequest(demande.id), "Demande abandonnée.", "supprimer", true))
     }

     const etat = demande.etat ?? ""
     const isRectif = [ETAT_RECTIF_PAR_DG, ETAT_RECTIF_PAR_HR].includes(etat)
     // Soumettre et abandonner : uniquement le créateur de la demande.
     const isOwner = demande.requester?.id === currentUser?.personnel?.id
     const isNonSalarie = (demande.personnel as any)?.statut === "STATUT_NON_SALARIE"
     const canSoumettre = isOwner && [ETAT_BROUILLON, ETAT_RECTIF_PAR_DG, ETAT_RECTIF_PAR_HR].includes(etat)
     const canDg = etat === ETAT_SOUMIS_DG && canActDg
     // Validation RH : salarié uniquement (le non salarié va directement au super-admin après la DG).
     const canHr = etat === ETAT_VALIDE_DG && !demande.creatorIsHr && !isNonSalarie && canActHr
     // Finalisation super-admin : après validation RH (salarié) ou dès validation DG (non salarié / créateur RH).
     const canFinalize = isSuperAdmin && (etat === ETAT_VALIDE_HR || (etat === ETAT_VALIDE_DG && (demande.creatorIsHr || isNonSalarie)))
     const canDelete = etat !== ETAT_FINALISE && isOwner
     // Envoi des documents d'embauche : réservé au créateur, pour un salarié en configuration IT
     // (VALIDE_HR, ou VALIDE_DG si initiée par les RH).
     const enConfigurationIT = etat === ETAT_VALIDE_HR || (etat === ETAT_VALIDE_DG && demande.creatorIsHr)
     const canEnvoyerDocsEmbauche = isOwner && !isNonSalarie && enConfigurationIT

     return (
          <div className={`d-flex flex-column flex-sm-row flex-sm-wrap gap-2 w-100 w-sm-auto ${className ?? ""}`}>
               {canSoumettre && (
                    <button className="btn btn-sm btn-primary w-100 w-sm-auto" disabled={busy} onClick={handleSoumettre}>
                         {spin("soumettre", isRectif ? "Envoyer" : "Soumettre")}
                    </button>
               )}
               {canDg && (
                    <>
                         <button className="btn btn-sm btn-success w-100 w-sm-auto" disabled={busy} onClick={handleValiderDg}>
                              {spin("validerDg", demande.isDgReexamen ? "Revalider" : "Valider")}
                         </button>
                         <button className="btn btn-sm btn-warning w-100 w-sm-auto" disabled={busy} onClick={handleRectifierDg}>
                              {spin("rectifierDg", "Demander une rectification")}
                         </button>
                    </>
               )}
               {canHr && (
                    <>
                         <button className="btn btn-sm btn-success w-100 w-sm-auto" disabled={busy} onClick={handleValiderHr}>
                              {spin("validerHr", "Valider")}
                         </button>
                         <button className="btn btn-sm btn-warning w-100 w-sm-auto" disabled={busy} onClick={handleRectifierHr}>
                              {spin("rectifierHr", "Demander une rectification")}
                         </button>
                    </>
               )}
               {canFinalize && (
                    <button className="btn btn-sm btn-dark w-100 w-sm-auto" disabled={busy} onClick={() => navigate(ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_FINALISER(demande.id))}>
                         Finaliser
                    </button>
               )}
               {canEnvoyerDocsEmbauche && (
                    <button className="btn btn-sm btn-success w-100 w-sm-auto" disabled={busy} onClick={() => navigate(ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_DOCUMENTS_EMBAUCHE(demande.id))}>
                         <i className="fas fa-paper-plane me-1" /> Envoyer les documents d'embauche
                    </button>
               )}
               {canDelete && (
                    <button className="btn btn-sm btn-light-danger w-100 w-sm-auto" disabled={busy} onClick={handleSupprimer}>
                         {spin("supprimer", <><i className="fas fa-trash" /> Abandonner</>)}
                    </button>
               )}
          </div>
     )
}

export default DemandeEmbaucheActions
